import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  MenuItem,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Tooltip,
  LinearProgress,
  Fade,
  Zoom,
  CardActions,
  CircularProgress,
  Skeleton,
  Menu,
  ListItemIcon,
  ListItemText,
  Popover,
  AlertTitle,
  Badge,
  Stack,
  Paper as MuiPaper,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Autocomplete,
  Collapse,
  IconButton as MuiIconButton,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  Dashboard as DashboardIcon,
  ShoppingCart as ShoppingCartIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  LockOutlined as LockOutlinedIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  ShoppingBag as ShoppingBagIcon,
  Star as StarIcon,
  StarHalf as StarHalfIcon,
  StarOutline as StarOutlineIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Store as StoreIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  HourglassEmpty as HourglassEmptyIcon,
  LocalOffer as LocalOfferIcon,
  Place as PlaceIcon,
  PersonOutline as PersonOutlineIcon,
  CalendarToday as CalendarTodayIcon,
  Receipt as ReceiptIcon,
  Send as SendIcon,
  Menu as MenuIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  Assignment as AssignmentIcon,
  Message as MessageIcon,
} from '@mui/icons-material';

// Import shared components
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { BRAND_COLORS, StatusChip } from '../buyers/components/styles';
import { formatCurrency, formatDate } from '../buyers/components/utils';

// ============================================
// ENHANCED COLOR SCHEME
// ============================================

const COLORS = {
  primary: '#1A237E',
  primaryLight: '#283593',
  primaryDark: '#0D1445',
  secondary: '#00BCD4',
  accent: '#FF6F00',
  success: '#2E7D32',
  warning: '#E65100',
  error: '#C62828',
  info: '#01579B',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// ============================================
// LOGGER
// ============================================

const logger = {
  info: (message, data) => console.log(`[SUPPLIER-CUSTOMERS] ✅ ${message}`, data || ''),
  error: (message, error) => console.error(`[SUPPLIER-CUSTOMERS] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[SUPPLIER-CUSTOMERS] 🔍 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[SUPPLIER-CUSTOMERS] ⚠️ ${message}`, data || ''),
};

// ============================================
// API CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

const API_ENDPOINTS = {
  CUSTOMERS: {
    LIST: `${API_BASE_URL}/suppliers/customers.php`,
    DETAILS: (id) => `${API_BASE_URL}/suppliers/customer-details.php?id=${id}`,
  },
  ORDERS: {
    LIST: `${API_BASE_URL}/orders/orders.php`,
  },
  SUPPLIER: {
    PROFILE: `${API_BASE_URL}/suppliers/profile.php`,
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const getStatusColor = (status) => {
  const colors = {
    'active': COLORS.success,
    'inactive': COLORS.error,
    'pending': COLORS.warning,
    'verified': COLORS.success,
    'unverified': COLORS.warning,
    'blocked': COLORS.error,
  };
  return colors[status] || COLORS.grey[600];
};

const getStatusLabel = (status) => {
  const labels = {
    'active': 'Active',
    'inactive': 'Inactive',
    'pending': 'Pending',
    'verified': 'Verified',
    'unverified': 'Unverified',
    'blocked': 'Blocked',
  };
  return labels[status] || status || 'N/A';
};

const formatDateShort = (date) => {
  if (!date) return 'N/A';
  try {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return 'N/A';
  }
};

const getInitials = (name) => {
  if (!name) return 'U';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// ============================================
// CUSTOM COMPONENTS
// ============================================

const StatCard = ({ icon, title, value, subtitle, color, trend, trendValue }) => {
  return (
    <Zoom in style={{ transitionDelay: '100ms' }}>
      <Card
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.02)} 100%)`,
          border: `1px solid ${alpha(color, 0.12)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 12px 40px ${alpha(color, 0.15)}`,
            borderColor: alpha(color, 0.3),
          },
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: COLORS.grey[600],
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  fontSize: '0.65rem',
                }}
              >
                {title}
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800,
                  mt: 0.5,
                  color: color,
                  fontSize: { xs: '1.8rem', sm: '2rem' },
                  lineHeight: 1.2,
                }}
              >
                {value}
              </Typography>
              {subtitle && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: COLORS.grey[600],
                    display: 'block',
                    mt: 0.25,
                    fontWeight: 500,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  {trend === 'up' ? (
                    <ArrowUpwardIcon sx={{ fontSize: 14, color: COLORS.success }} />
                  ) : trend === 'down' ? (
                    <ArrowDownwardIcon sx={{ fontSize: 14, color: COLORS.error }} />
                  ) : null}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: trend === 'up' ? COLORS.success : trend === 'down' ? COLORS.error : COLORS.grey[600],
                      fontWeight: 700,
                    }}
                  >
                    {trendValue}
                  </Typography>
                </Box>
              )}
            </Box>
            <Avatar
              sx={{
                bgcolor: alpha(color, 0.12),
                color: color,
                width: 48,
                height: 48,
                border: `2px solid ${alpha(color, 0.15)}`,
                '& .MuiSvgIcon-root': { fontSize: 24 },
              }}
            >
              {icon}
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </Zoom>
  );
};

const CustomerCard = ({ customer, onView, onContact }) => {
  const [expanded, setExpanded] = useState(false);
  
  const statusColor = getStatusColor(customer.status || 'active');
  const initial = getInitials(customer.name);
  
  return (
    <Fade in>
      <Card
        sx={{
          mb: 2.5,
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${COLORS.grey[200]}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: '#FFFFFF',
          '&:hover': {
            boxShadow: '0 16px 48px rgba(0,0,0,0.08)',
            borderColor: COLORS.primary,
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            {/* Left section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(COLORS.primary, 0.08),
                  color: COLORS.primary,
                  width: 52,
                  height: 52,
                  border: `2px solid ${alpha(COLORS.primary, 0.1)}`,
                  fontWeight: 800,
                  fontSize: '1.2rem',
                }}
              >
                {initial}
              </Avatar>
              <Box>
                <Typography 
                  variant="subtitle1" 
                  fontWeight={800} 
                  sx={{ 
                    color: COLORS.grey[900],
                    fontSize: '1.05rem',
                    letterSpacing: '-0.3px',
                  }}
                >
                  {customer.name || 'Unknown Customer'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
                  <BusinessIcon sx={{ fontSize: 14, color: COLORS.grey[500] }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: COLORS.grey[700],
                      fontWeight: 600,
                    }}
                  >
                    {customer.organisation || 'N/A'}
                  </Typography>
                  {customer.industry && (
                    <>
                      <Typography variant="caption" color={COLORS.grey[400]}>•</Typography>
                      <Chip
                        label={customer.industry}
                        size="small"
                        sx={{
                          height: 20,
                          bgcolor: alpha(COLORS.info, 0.08),
                          color: COLORS.info,
                          fontWeight: 600,
                          fontSize: '0.6rem',
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Box>
            
            {/* Right section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip
                label={getStatusLabel(customer.status || 'active')}
                size="small"
                sx={{
                  bgcolor: alpha(statusColor, 0.1),
                  color: statusColor,
                  fontWeight: 700,
                  height: 26,
                  border: `1px solid ${alpha(statusColor, 0.15)}`,
                  '& .MuiChip-label': { fontSize: '0.7rem', px: 1.5, fontWeight: 700 },
                }}
              />
              {customer.total_orders > 0 && (
                <Chip
                  icon={<ShoppingBagIcon sx={{ fontSize: 14 }} />}
                  label={`${customer.total_orders} orders`}
                  size="small"
                  sx={{
                    bgcolor: alpha(COLORS.primary, 0.06),
                    color: COLORS.primary,
                    fontWeight: 600,
                    height: 26,
                    '& .MuiChip-label': { fontSize: '0.7rem', px: 1.5, fontWeight: 600 },
                  }}
                />
              )}
              <Chip
                icon={<AttachMoneyIcon sx={{ fontSize: 14 }} />}
                label={formatCurrency(customer.total_spent || 0)}
                size="small"
                sx={{
                  bgcolor: alpha(COLORS.accent, 0.08),
                  color: COLORS.accent,
                  fontWeight: 700,
                  height: 26,
                  '& .MuiChip-label': { fontSize: '0.7rem', px: 1.5, fontWeight: 700 },
                }}
              />
            </Box>
          </Box>
          
          {/* Customer Details */}
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mt: 2.5, pt: 2.5, borderTop: `1px solid ${COLORS.grey[100]}` }}>
            {customer.email && (
              <Box>
                <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                  <EmailIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                  Email
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[800], mt: 0.25 }}>
                  {customer.email}
                </Typography>
              </Box>
            )}
            {customer.phone && (
              <Box>
                <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                  <PhoneIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                  Phone
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[800], mt: 0.25 }}>
                  {customer.phone}
                </Typography>
              </Box>
            )}
            {customer.preferred_contact_method && (
              <Box>
                <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                  <MessageIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                  Preferred Contact
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[800], mt: 0.25, textTransform: 'capitalize' }}>
                  {customer.preferred_contact_method}
                </Typography>
              </Box>
            )}
            {customer.first_order_date && (
              <Box>
                <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                  <CalendarTodayIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                  First Order
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[800], mt: 0.25 }}>
                  {formatDateShort(customer.first_order_date)}
                </Typography>
              </Box>
            )}
            {customer.last_order_date && (
              <Box>
                <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                  <ScheduleIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                  Last Order
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[800], mt: 0.25 }}>
                  {formatDateShort(customer.last_order_date)}
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Expandable */}
          <Collapse in={expanded} sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[700], mb: 1.5 }}>
              Recent Orders
            </Typography>
            <Box sx={{ bgcolor: COLORS.grey[50], borderRadius: 2, p: 2.5, border: `1px solid ${COLORS.grey[200]}` }}>
              {customer.recent_orders && customer.recent_orders.length > 0 ? (
                customer.recent_orders.map((order, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: idx < customer.recent_orders.length - 1 ? `1px solid ${COLORS.grey[200]}` : 'none' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: COLORS.grey[700] }}>
                      #{order.id}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.primary }}>
                      {formatCurrency(order.total_amount)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: COLORS.grey[500] }}>
                      {formatDateShort(order.order_date)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color={COLORS.grey[500]}>No recent orders</Typography>
              )}
            </Box>
          </Collapse>
          
          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2, pt: 2, borderTop: `1px solid ${COLORS.grey[100]}` }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setExpanded(!expanded)}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600,
                color: COLORS.grey[600],
                '&:hover': { color: COLORS.primary },
              }}
            >
              {expanded ? 'Show Less' : 'Show Recent Orders'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => onContact(customer)}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none',
                fontWeight: 600,
                ml: 1,
                borderColor: COLORS.grey[300],
                color: COLORS.grey[700],
                '&:hover': {
                  borderColor: COLORS.primary,
                  color: COLORS.primary,
                  bgcolor: alpha(COLORS.primary, 0.04),
                },
              }}
            >
              Contact
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => onView(customer)}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none',
                fontWeight: 700,
                ml: 1,
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                boxShadow: `0 4px 16px ${alpha(COLORS.primary, 0.25)}`,
                '&:hover': {
                  boxShadow: `0 8px 24px ${alpha(COLORS.primary, 0.35)}`,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              View Details
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const SupplierCustomers = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // ============================================
  // STATE
  // ============================================

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetailsDialog, setCustomerDetailsDialog] = useState(false);
  const [contactDialog, setContactDialog] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('customers');
  const [userData, setUserData] = useState(null);
  const [supplierData, setSupplierData] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // UI states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [viewMode, setViewMode] = useState('table');
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [contactMessage, setContactMessage] = useState('');

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    totalRevenue: 0,
    averageOrderValue: 0,
  });

  // ============================================
  // API FUNCTIONS
  // ============================================

  const fetchSupplierData = useCallback(async () => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) return;

      const url = `${API_ENDPOINTS.SUPPLIER.PROFILE}?user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (result.success) {
        setSupplierData(result.data);
      }
    } catch (error) {
      logger.error('Error fetching supplier data:', error);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    logger.info('Fetching customers...');
    setLoading(true);

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const url = `${API_ENDPOINTS.CUSTOMERS.LIST}?supplier_id=${userId}&user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        let customersData = [];

        if (Array.isArray(result.data)) {
          customersData = result.data;
        } else if (result.data && typeof result.data === 'object') {
          if (Array.isArray(result.data.data)) {
            customersData = result.data.data;
          } else if (Array.isArray(result.data.customers)) {
            customersData = result.data.customers;
          } else {
            const arrayKey = Object.keys(result.data).find((key) => Array.isArray(result.data[key]));
            if (arrayKey) {
              customersData = result.data[arrayKey];
            }
          }
        } else if (Array.isArray(result)) {
          customersData = result;
        }

        setCustomers(customersData);
        calculateStats(customersData);
        logger.info(`✅ Loaded ${customersData.length} customers`);
      } else {
        throw new Error(result.message || 'Failed to fetch customers');
      }
    } catch (error) {
      logger.error('Error fetching customers:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load customers. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const calculateStats = (customersData) => {
    const statsData = {
      total: customersData.length,
      active: customersData.filter(c => c.status === 'active' || !c.status).length,
      inactive: customersData.filter(c => c.status === 'inactive').length,
      totalRevenue: customersData.reduce((sum, c) => sum + (c.total_spent || 0), 0),
      averageOrderValue: customersData.length > 0 
        ? customersData.reduce((sum, c) => sum + (c.total_spent || 0), 0) / customersData.length 
        : 0,
    };
    setStats(statsData);
  };

  // ============================================
  // FILTERING & SORTING
  // ============================================

  useEffect(() => {
    let result = [...customers];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(customer => 
        (customer.name && customer.name.toLowerCase().includes(term)) ||
        (customer.organisation && customer.organisation.toLowerCase().includes(term)) ||
        (customer.email && customer.email.toLowerCase().includes(term)) ||
        (customer.industry && customer.industry.toLowerCase().includes(term))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(customer => {
        const status = customer.status || 'active';
        return status === statusFilter;
      });
    }

    // Industry filter
    if (industryFilter !== 'all') {
      result = result.filter(customer => 
        customer.industry && customer.industry.toLowerCase() === industryFilter.toLowerCase()
      );
    }

    // Sorting
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.created_at || b.first_order_date || 0) - new Date(a.created_at || a.first_order_date || 0));
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.created_at || a.first_order_date || 0) - new Date(b.created_at || b.first_order_date || 0));
        break;
      case 'highest':
        result.sort((a, b) => (b.total_spent || 0) - (a.total_spent || 0));
        break;
      case 'lowest':
        result.sort((a, b) => (a.total_spent || 0) - (b.total_spent || 0));
        break;
      case 'most_orders':
        result.sort((a, b) => (b.total_orders || 0) - (a.total_orders || 0));
        break;
      default:
        break;
    }

    setFilteredCustomers(result);
  }, [customers, searchTerm, statusFilter, industryFilter, sortBy]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userType = localStorage.getItem('user_type');
    
    if (!token || userType !== 'supplier') {
      navigate('/RawAuth');
      return;
    }

    const userDataStr = localStorage.getItem('user_data');
    if (userDataStr) {
      try {
        setUserData(JSON.parse(userDataStr));
      } catch (e) {
        logger.error('Error parsing user data:', e);
      }
    }

    fetchSupplierData();
    fetchCustomers();
  }, [fetchCustomers, fetchSupplierData, navigate]);

  // ============================================
  // HANDLERS
  // ============================================

// ============================================
// NAVIGATION HANDLER
// ============================================
const handleSidebarNavigation = useCallback((path) => {
  // Update the selected tab state
  setSelectedTab(path);
  // Navigate to the path
  navigate(path);
  // Close drawer on mobile
  if (isMobile) setDrawerOpen(false);
}, [navigate, isMobile]);


  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/RawAuth');
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'info' });
  }, [navigate]);

  const handleChangePassword = useCallback(() => {
    setChangePasswordDialog(true);
    setProfileAnchorEl(null);
  }, []);

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerDetailsDialog(true);
  };

  const handleContactCustomer = (customer) => {
    setSelectedCustomer(customer);
    setContactMessage('');
    setContactDialog(true);
  };

  const handleSendMessage = () => {
    if (!contactMessage.trim()) {
      setSnackbar({ open: true, message: 'Please enter a message', severity: 'error' });
      return;
    }
    
    // Here you would implement the actual messaging functionality
    setSnackbar({ 
      open: true, 
      message: `Message sent to ${selectedCustomer?.name}`, 
      severity: 'success' 
    });
    setContactDialog(false);
    setContactMessage('');
  };

  const handleRefresh = () => {
    fetchCustomers();
    setSnackbar({ open: true, message: 'Customers refreshed', severity: 'info' });
  };

  const handleExport = () => {
    const headers = ['Name', 'Organisation', 'Email', 'Phone', 'Industry', 'Total Orders', 'Total Spent', 'Status'];
    const rows = filteredCustomers.map(customer => [
      customer.name || 'N/A',
      customer.organisation || 'N/A',
      customer.email || 'N/A',
      customer.phone || 'N/A',
      customer.industry || 'N/A',
      customer.total_orders || 0,
      customer.total_spent || 0,
      getStatusLabel(customer.status || 'active'),
    ]);
    
    let csv = headers.join(',') + '\n';
    rows.forEach(row => {
      csv += row.join(',') + '\n';
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    setSnackbar({ open: true, message: 'Customers exported successfully', severity: 'success' });
  };

  // ============================================
  // RENDER FUNCTIONS
  // ============================================

  const renderStats = () => {
    const statItems = [
      { 
        icon: <PeopleIcon />, 
        title: 'Total Customers', 
        value: stats.total,
        color: COLORS.primary,
      },
      { 
        icon: <VerifiedIcon />, 
        title: 'Active', 
        value: stats.active,
        subtitle: `${((stats.active / stats.total) * 100).toFixed(0)}% of total`,
        color: COLORS.success,
        trend: stats.active > 0 ? 'up' : null,
        trendValue: stats.active > 0 ? `${stats.active} active` : 'No active',
      },
      { 
        icon: <AttachMoneyIcon />, 
        title: 'Total Revenue', 
        value: formatCurrency(stats.totalRevenue),
        subtitle: `From ${stats.total} customers`,
        color: COLORS.accent,
      },
      { 
        icon: <AssessmentIcon />, 
        title: 'Avg Order Value', 
        value: formatCurrency(stats.averageOrderValue),
        subtitle: 'Per customer lifetime',
        color: COLORS.info,
      },
    ];

    return (
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        {statItems.map((item, index) => (
          <Grid size={{ xs: 6, sm: 6, md: 3 }} key={index}>
            <StatCard {...item} />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderFilters = () => {
    // Get unique industries for filter
    const industries = [...new Set(customers.map(c => c.industry).filter(Boolean))];
    
    return (
      <Paper 
        sx={{ 
          p: 2.5, 
          mb: 3.5, 
          borderRadius: 4,
          border: `1px solid ${COLORS.grey[200]}`,
          background: '#FFFFFF',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <TextField
          placeholder="Search customers by name, email..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: COLORS.grey[500] }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" sx={{ color: COLORS.grey[500] }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ 
            minWidth: 220, 
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: COLORS.primary,
              },
            },
          }}
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              '& .MuiSelect-select': { py: 1.25 },
            }}
          >
            <MenuItem value="all" sx={{ fontWeight: 600 }}>All Statuses</MenuItem>
            <MenuItem value="active" sx={{ fontWeight: 500 }}>Active</MenuItem>
            <MenuItem value="inactive" sx={{ fontWeight: 500 }}>Inactive</MenuItem>
            <MenuItem value="pending" sx={{ fontWeight: 500 }}>Pending</MenuItem>
            <MenuItem value="blocked" sx={{ fontWeight: 500 }}>Blocked</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={industryFilter}
            onChange={(e) => setIndustryFilter(e.target.value)}
            displayEmpty
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              '& .MuiSelect-select': { py: 1.25 },
            }}
          >
            <MenuItem value="all" sx={{ fontWeight: 600 }}>All Industries</MenuItem>
            {industries.map((industry) => (
              <MenuItem key={industry} value={industry} sx={{ fontWeight: 500 }}>
                {industry}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            displayEmpty
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              '& .MuiSelect-select': { py: 1.25 },
            }}
          >
            <MenuItem value="newest" sx={{ fontWeight: 600 }}>Newest First</MenuItem>
            <MenuItem value="oldest" sx={{ fontWeight: 500 }}>Oldest First</MenuItem>
            <MenuItem value="highest" sx={{ fontWeight: 500 }}>Highest Spent</MenuItem>
            <MenuItem value="lowest" sx={{ fontWeight: 500 }}>Lowest Spent</MenuItem>
            <MenuItem value="most_orders" sx={{ fontWeight: 500 }}>Most Orders</MenuItem>
          </Select>
        </FormControl>
        
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Tooltip title="Refresh">
            <IconButton 
              onClick={handleRefresh} 
              size="small"
              sx={{ 
                bgcolor: COLORS.grey[100],
                borderRadius: 2,
                '&:hover': { bgcolor: COLORS.grey[200] },
              }}
            >
              <RefreshIcon sx={{ color: COLORS.grey[700] }} />
            </IconButton>
          </Tooltip>
        
        </Box>
      </Paper>
    );
  };

  const renderTable = () => (
    <TableContainer 
      component={Paper} 
      sx={{ 
        borderRadius: 4,
        border: `1px solid ${COLORS.grey[200]}`,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
      }}
    >
      <Table>
        <TableHead sx={{ bgcolor: COLORS.grey[50] }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Customer
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Organisation
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Industry
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Orders
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Spent
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Status
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }} align="center">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredCustomers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                <Typography color={COLORS.grey[500]} fontWeight={600}>No customers found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredCustomers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((customer) => {
                const statusColor = getStatusColor(customer.status || 'active');
                return (
                  <TableRow 
                    key={customer.id}
                    hover
                    sx={{ 
                      '&:hover': { bgcolor: alpha(COLORS.primary, 0.02) },
                      cursor: 'pointer',
                      transition: 'background 0.2s ease',
                    }}
                    onClick={() => handleViewCustomer(customer)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(COLORS.primary, 0.08),
                            color: COLORS.primary,
                            width: 32,
                            height: 32,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                          }}
                        >
                          {getInitials(customer.name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.grey[900] }}>
                            {customer.name || 'Unknown'}
                          </Typography>
                          {customer.email && (
                            <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 500 }}>
                              {customer.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                        {customer.organisation || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={customer.industry || 'N/A'}
                        size="small"
                        sx={{
                          bgcolor: alpha(COLORS.info, 0.08),
                          color: COLORS.info,
                          fontWeight: 600,
                          height: 22,
                          '& .MuiChip-label': { fontSize: '0.65rem', px: 1.5, fontWeight: 600 },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.primary }}>
                        {customer.total_orders || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.accent }}>
                        {formatCurrency(customer.total_spent || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(customer.status || 'active')}
                        size="small"
                        sx={{
                          bgcolor: alpha(statusColor, 0.1),
                          color: statusColor,
                          fontWeight: 700,
                          height: 22,
                          border: `1px solid ${alpha(statusColor, 0.15)}`,
                          '& .MuiChip-label': { fontSize: '0.65rem', px: 1.5, fontWeight: 700 },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewCustomer(customer);
                            }}
                            sx={{ 
                              color: COLORS.primary,
                              bgcolor: alpha(COLORS.primary, 0.04),
                              '&:hover': { bgcolor: alpha(COLORS.primary, 0.12) },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Contact">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleContactCustomer(customer);
                            }}
                            sx={{ 
                              color: COLORS.success,
                              bgcolor: alpha(COLORS.success, 0.04),
                              '&:hover': { bgcolor: alpha(COLORS.success, 0.12) },
                            }}
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredCustomers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{
          borderTop: `1px solid ${COLORS.grey[200]}`,
          '& .MuiTablePagination-select': { fontWeight: 600 },
        }}
      />
    </TableContainer>
  );

  const renderCardView = () => (
    <Box>
      {filteredCustomers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" sx={{ color: COLORS.grey[500], fontWeight: 600 }}>No customers found</Typography>
        </Box>
      ) : (
        filteredCustomers
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((customer) => (
            <CustomerCard
              key={customer.id}
              customer={customer}
              onView={handleViewCustomer}
              onContact={handleContactCustomer}
            />
          ))
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredCustomers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{
          borderTop: `1px solid ${COLORS.grey[200]}`,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          mt: 2,
          '& .MuiTablePagination-select': { fontWeight: 600 },
        }}
      />
    </Box>
  );

  const renderCustomerDetailsDialog = () => (
    <Dialog
      open={customerDetailsDialog}
      onClose={() => setCustomerDetailsDialog(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 4, 
          maxHeight: '90vh',
          background: '#FFFFFF',
          boxShadow: '0 32px 64px rgba(0,0,0,0.15)',
        } 
      }}
    >
      {selectedCustomer && (
        <>
          <DialogTitle sx={{ p: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(COLORS.primary, 0.08),
                    color: COLORS.primary,
                    width: 52,
                    height: 52,
                    fontWeight: 800,
                    fontSize: '1.2rem',
                    border: `2px solid ${alpha(COLORS.primary, 0.1)}`,
                  }}
                >
                  {getInitials(selectedCustomer.name)}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={800} sx={{ color: COLORS.grey[900] }}>
                    {selectedCustomer.name || 'Unknown Customer'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: COLORS.grey[500], fontWeight: 500 }}>
                    {selectedCustomer.organisation || 'No organisation'}
                  </Typography>
                </Box>
              </Box>
              <IconButton 
                onClick={() => setCustomerDetailsDialog(false)}
                sx={{ 
                  bgcolor: COLORS.grey[100],
                  '&:hover': { bgcolor: COLORS.grey[200] },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <PersonOutlineIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Contact Information
                  </Typography>
                  {selectedCustomer.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: COLORS.grey[500] }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                        {selectedCustomer.email}
                      </Typography>
                    </Box>
                  )}
                  {selectedCustomer.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: COLORS.grey[500] }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                        {selectedCustomer.phone}
                      </Typography>
                    </Box>
                  )}
                  {selectedCustomer.preferred_contact_method && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Chip
                        label={`Preferred: ${selectedCustomer.preferred_contact_method}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(COLORS.info, 0.08),
                          color: COLORS.info,
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <AssessmentIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Customer Stats
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                        Total Orders
                      </Typography>
                      <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.primary }}>
                        {selectedCustomer.total_orders || 0}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                        Total Spent
                      </Typography>
                      <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.accent }}>
                        {formatCurrency(selectedCustomer.total_spent || 0)}
                      </Typography>
                    </Grid>
                  </Grid>
                  {selectedCustomer.average_order_value && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                        Avg Order Value
                      </Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.grey[700] }}>
                        {formatCurrency(selectedCustomer.average_order_value)}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <BusinessIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Business Information
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                    <strong>Organisation:</strong> {selectedCustomer.organisation || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                    <strong>Industry:</strong> {selectedCustomer.industry || 'N/A'}
                  </Typography>
                  {selectedCustomer.delivery_address && (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                      <strong>Delivery Address:</strong> {selectedCustomer.delivery_address}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <CalendarTodayIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Activity Timeline
                  </Typography>
                  {selectedCustomer.first_order_date && (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                      <strong>First Order:</strong> {formatDateShort(selectedCustomer.first_order_date)}
                    </Typography>
                  )}
                  {selectedCustomer.last_order_date && (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                      <strong>Last Order:</strong> {formatDateShort(selectedCustomer.last_order_date)}
                    </Typography>
                  )}
                  {selectedCustomer.created_at && (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                      <strong>Customer Since:</strong> {formatDateShort(selectedCustomer.created_at)}
                    </Typography>
                  )}
                  {selectedCustomer.newsletter && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        icon={<SendIcon sx={{ fontSize: 14 }} />}
                        label="Subscribed to Newsletter"
                        size="small"
                        sx={{
                          bgcolor: alpha(COLORS.success, 0.08),
                          color: COLORS.success,
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Special Requirements */}
              {selectedCustomer.special_requirements && (
                <Grid size={{ xs: 12 }}>
                  <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                      <AssignmentIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                      Special Requirements
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.grey[700] }}>
                      {selectedCustomer.special_requirements}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 2 }}>
            <Button 
              onClick={() => setCustomerDetailsDialog(false)}
              sx={{ 
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={() => {
                setCustomerDetailsDialog(false);
                handleContactCustomer(selectedCustomer);
              }}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none',
                fontWeight: 700,
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                boxShadow: `0 4px 16px ${alpha(COLORS.primary, 0.25)}`,
                '&:hover': {
                  boxShadow: `0 8px 24px ${alpha(COLORS.primary, 0.35)}`,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Contact Customer
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  const renderContactDialog = () => (
    <Dialog
      open={contactDialog}
      onClose={() => setContactDialog(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 4,
          background: '#FFFFFF',
          boxShadow: '0 32px 64px rgba(0,0,0,0.15)',
        } 
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EmailIcon sx={{ color: COLORS.primary }} />
            <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.grey[900] }}>
              Contact Customer
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setContactDialog(false)}
            sx={{ 
              bgcolor: COLORS.grey[100],
              '&:hover': { bgcolor: COLORS.grey[200] },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 1 }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="body2" sx={{ color: COLORS.grey[600], fontWeight: 500 }}>
            Sending message to:
          </Typography>
          <Typography variant="body1" fontWeight={700} sx={{ color: COLORS.grey[900] }}>
            {selectedCustomer?.name}
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.grey[500] }}>
            {selectedCustomer?.email}
          </Typography>
        </Box>
        
        <TextField
          label="Message"
          placeholder="Type your message here..."
          multiline
          rows={4}
          fullWidth
          value={contactMessage}
          onChange={(e) => setContactMessage(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: COLORS.primary,
              },
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button 
          onClick={() => setContactDialog(false)}
          sx={{ 
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSendMessage}
          sx={{ 
            borderRadius: 2, 
            textTransform: 'none',
            fontWeight: 700,
            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
            boxShadow: `0 4px 16px ${alpha(COLORS.primary, 0.25)}`,
            '&:hover': {
              boxShadow: `0 8px 24px ${alpha(COLORS.primary, 0.35)}`,
              transform: 'translateY(-1px)',
            },
          }}
        >
          Send Message
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderChangePasswordDialog = () => (
    <Dialog
      open={changePasswordDialog}
      onClose={() => setChangePasswordDialog(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 4,
          background: '#FFFFFF',
          boxShadow: '0 32px 64px rgba(0,0,0,0.15)',
        } 
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LockOutlinedIcon sx={{ color: COLORS.primary }} />
          <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.grey[900] }}>
            Change Password
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <TextField 
            label="Current Password" 
            type="password" 
            fullWidth 
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              },
            }}
          />
          <TextField 
            label="New Password" 
            type="password" 
            fullWidth 
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              },
            }}
          />
          <TextField 
            label="Confirm New Password" 
            type="password" 
            fullWidth 
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button 
          onClick={() => setChangePasswordDialog(false)}
          sx={{ 
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          sx={{ 
            bgcolor: COLORS.primary,
            fontWeight: 700,
            borderRadius: 2,
            px: 4,
            boxShadow: `0 4px 16px ${alpha(COLORS.primary, 0.3)}`,
            '&:hover': { 
              bgcolor: COLORS.primaryLight,
              boxShadow: `0 6px 24px ${alpha(COLORS.primary, 0.4)}`,
            },
          }}
        >
          Update Password
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  if (loading && customers.length === 0) {
    return <Preloader text="Loading Customers..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: COLORS.grey[50] }}>
      <Sidebar
  open={drawerOpen}
  onClose={() => setDrawerOpen(false)}
  isMobile={isMobile}
  selectedTab={selectedTab}
  onTabChange={handleSidebarNavigation}  // ← Use the handler instead of setSelectedTab
  userData={userData}
  onLogout={() => {
    localStorage.clear();
    navigate('/RawAuth');
  }}
/>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 280px)' },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={[]}
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          cartItemCount={0}
          onCartOpen={() => {}}
          notifications={[]}
          userData={userData}
          onLogout={handleLogout}
          onChangePassword={handleChangePassword}
       
        />

        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          {/* Header */}
          <Box sx={{ mb: 3.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography 
                  variant="h4" 
                  fontWeight={800} 
                  sx={{ 
                    color: COLORS.grey[900],
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                  }}
                >
                  <PeopleIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
                  Customer Management
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.grey[500], fontWeight: 500, mt: 0.25 }}>
                  {filteredCustomers.length} customers found • Last updated: {lastRefreshed.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: COLORS.grey[300],
                    color: COLORS.grey[700],
                    '&:hover': {
                      borderColor: COLORS.primary,
                      color: COLORS.primary,
                      bgcolor: alpha(COLORS.primary, 0.04),
                    },
                  }}
                >
                  Refresh
                </Button>
               
              </Box>
            </Box>
          </Box>

          {/* Stats */}
          {renderStats()}

          {/* Filters */}
          {renderFilters()}

          {/* View Mode Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant={viewMode === 'table' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('table')}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  fontWeight: 700,
                  ...(viewMode === 'table' && {
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                    boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.25)}`,
                  }),
                }}
              >
                Table View
              </Button>
              <Button
                size="small"
                variant={viewMode === 'card' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('card')}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  fontWeight: 700,
                  ...(viewMode === 'card' && {
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                    boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.25)}`,
                  }),
                }}
              >
                Card View
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600 }}>
              Showing {Math.min(filteredCustomers.length, rowsPerPage)} of {filteredCustomers.length} customers
            </Typography>
          </Box>

          {/* Customers List */}
          {viewMode === 'table' ? renderTable() : renderCardView()}

          {/* Dialogs */}
          {renderCustomerDetailsDialog()}
          {renderContactDialog()}
          {renderChangePasswordDialog()}
        </Box>

        <Footer />
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
            fontWeight: 600,
            '& .MuiAlert-icon': { 
              alignItems: 'center',
              fontSize: 24,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Global styles for animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
          }
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          ::-webkit-scrollbar-track {
            background: ${COLORS.grey[100]};
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb {
            background: ${COLORS.grey[300]};
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: ${COLORS.grey[400]};
          }
          @media print {
            .MuiPaper-root {
              box-shadow: none !important;
              border: 1px solid ${COLORS.grey[300]} !important;
            }
            .MuiButton-root {
              display: none !important;
            }
            .MuiIconButton-root {
              display: none !important;
            }
            .MuiTablePagination-root {
              display: none !important;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default SupplierCustomers;